import { useParams } from "react-router-dom";
import useOverAllCosts from "../../../api/api-hooks/brieCostTracker/useOverAll";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import AppEmptyState from "../../../elements/AppEmptyState";
import { Input } from "reactstrap";
import { useMemo, useState } from "react";
import CostsTable from "./CostsTable";
import YearPicker from "../../../components/Inputs/YearPicker";
import CostGraph from "./CostsGraph";
import Constants from "../../../utils/Constants";
import ToggleInput from "../../../components/Inputs/ToggleInputCostTracker";
import dayjs from "dayjs";

const OverAllCloudCosts = () => {
  const [graphView, setGraphView] = useState(false);
  const [type, setType] = useState<"month" | "year">("month");

  const { cloudID } = useParams();
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const { costs, costsLoading } = useOverAllCosts(
    type,
    selectedMonth,
    selectedYear,
    cloudID
  );

  const parsedCosts = useMemo(() => {
    if (!Array.isArray(costs)) return [];

    return costs?.map((cost: any) => {
      return {
        Owner: cost.Owner || cost.Month,
        Month: cost.Month,
        Created: cost.Created,
        RecordName: `${cost?.Owner?.split?.(" ")?.[0] || "User"}'s ${dayjs(
          cost.Created
        ).format("MMMM YYYY")} Usage`,
        ...getCostsPerModel(cost.Models),
      };
    });
  }, [costs, type, selectedMonth, selectedYear]);

  return (
    <div className="mx-3" style={{ height: "calc(100vh - 120px)" }}>
      <>
        <div className="d-flex align-items-center justify-content-between pb-3">
          <div className="d-flex ">
            <Input
              style={{ maxWidth: "150px" }}
              className="mr-2"
              type="select"
              value={type}
              onChange={(e) => setType(e.target.value as "month" | "year")}
              bsSize="sm"
            >
              <option value="year">By Year</option>
              <option value="month">By Month</option>
            </Input>
            {type === "month" ? (
              <Input
                style={{ maxWidth: "130px", fontSize: "12px" }}
                onChange={(e) => {
                  setSelectedMonth(parseInt(e.target.value.split("-")[1]));
                  setSelectedYear(parseInt(e.target.value.split("-")[0]));
                }}
                className="cursor-pointer"
                value={`${selectedYear}-${
                  selectedMonth < 10 ? "0" : ""
                }${selectedMonth}`}
                type="month"
                bsSize="sm"
              />
            ) : (
              <YearPicker
                selectedYear={selectedYear}
                handleYearChange={(year) => setSelectedYear(year)}
              />
            )}
          </div>
          <ToggleInput
            value={graphView}
            icons={{
              checked: "mdi mdi-chart-bar",
              unchecked: "mdi mdi-table",
            }}
            options={{ checked: "Graph", unchecked: "Table" }}
            onChange={(value) => setGraphView(value)}
          />
        </div>
        {!costsLoading ? (
          <>
            {cloudID && parsedCosts ? (
              <div
                key={`${selectedYear}-${selectedMonth}`}
                className="pt-1 pb-1"
                style={{ height: "80%", overflow: "auto" }}
              >
                <div style={{ height: "calc(100% - 73px)" }}>
                  {graphView ? (
                    <CostGraph
                      type={type}
                      selectedMonth={selectedMonth}
                      selectedYear={selectedYear}
                      parsedCosts={parsedCosts}
                    />
                  ) : (
                    <CostsTable
                      recordsLoading={!parsedCosts}
                      parsedCosts={parsedCosts}
                      year={selectedYear}
                      month={selectedMonth}
                      type={type}
                    />
                  )}
                </div>
              </div>
            ) : (
              <AppEmptyState
                title={"No Brie Usage"}
                description="Brie has not been used in this cloud yet."
              />
            )}
          </>
        ) : (
          <SkeletonLoader height="50%" />
        )}
      </>
    </div>
  );

  function getCostsPerModel(
    models: Record<string, { InputTokens: number; OutputTokens: number }>
  ): Record<string, number | string> {
    const allModels = Object.keys(Constants.MODELS_COST);

    return allModels.reduce(
      (acc: Record<string, number | string>, model: string) => {
        const total = calculateCostPerToken({
          inputTokens: models[model]?.InputTokens,
          outputTokens: models[model]?.OutputTokens,
          model: model as keyof typeof Constants.MODELS_COST,
        });

        acc[model] = `$${total}`;
        acc[`${model}-graph`] = parseFloat(total);
        return acc;
      },
      {}
    );
  }

  function calculateCostPerToken({
    inputTokens,
    outputTokens,
    model,
  }: {
    inputTokens?: number;
    outputTokens?: number;
    model: keyof typeof Constants.MODELS_COST;
  }) {
    inputTokens = inputTokens || 0;
    outputTokens = outputTokens || 0;

    return (
      Constants.MODELS_COST[model].input * inputTokens +
      Constants.MODELS_COST[model].output * outputTokens
    ).toFixed(6);
  }
};

export default OverAllCloudCosts;
